class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Use event delegation on `this` (the custom element) instead of the inner form,
    // because AJAX re-renders replace the inner form element, losing listeners attached to it.
    this.addEventListener("input", (evt) => {
      const form = evt.target.closest("form");
      if (!form) return;
      FacetFiltersForm.loading.start();
      this.onSubmitHandler(evt);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    const onPageShow = (event) => {
      const navigationEntry =
        window.performance &&
        window.performance.getEntriesByType &&
        window.performance.getEntriesByType("navigation")[0];
      const isHistoryRestore =
        event.persisted ||
        (navigationEntry && navigationEntry.type === "back_forward");

      if (!isHistoryRestore || !FacetFiltersForm.sectionId) return;

      FacetFiltersForm.renderPage(
        window.location.search.slice(1),
        null,
        false,
        false
      );
    };

    window.addEventListener("popstate", onHistoryChange);
    window.addEventListener("pageshow", onPageShow);
  }

  static finishLoading() {
    try {
      FacetFiltersForm.loading?.finish?.();
    } catch (error) {
      console.warn("Facet loader could not be finished cleanly", error);
    }
  }

  static renderPage(
    searchParams,
    event,
    updateURLHash = true,
    scrollToResults = true
  ) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (scrollToResults) {
      window.scroll({
        top: FacetFiltersForm.section.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed with HTTP ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderElements(
          html,
          "[data-product-container]",
          FacetFiltersForm.selectors.productGrid
        );
        FacetFiltersForm.renderElements(
          html,
          "[data-facet-form]",
          FacetFiltersForm.selectors.filtersForm
        );
        FacetFiltersForm.renderProductCount(html);

        document.dispatchEvent(new CustomEvent("collection:rerendered"));
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          console.warn("Collection filter request timed out and was cancelled.", url);
        } else {
          console.error("Collection filter request failed.", error);
        }
      })
      .finally(() => {
        window.clearTimeout(timeout);
        FacetFiltersForm.finishLoading();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderElements(
        html,
        "[data-product-container]",
        FacetFiltersForm.selectors.productGrid
      );
      FacetFiltersForm.renderElements(
        html,
        "[data-facet-form]",
        FacetFiltersForm.selectors.filtersForm
      );
      FacetFiltersForm.renderProductCount(html);

      document.dispatchEvent(new CustomEvent("collection:rerendered"));
    } finally {
      FacetFiltersForm.finishLoading();
    }
  }

  static renderElements(html, el, selector) {
    const elementContainer = document.querySelector(el);
    const parsedContainer = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(el);
    if (!elementContainer || !parsedContainer) {
      throw new Error(`Facet render target missing: ${el}`);
    }
    elementContainer.innerHTML = parsedContainer.innerHTML;
    FacetFiltersForm.loading = new window.FoxTheme.AnimateLoading(
      document.body,
      { overlay: document.querySelector(selector) }
    );
    __reInitTooltip(elementContainer);
  }

  static renderProductCount(html) {
    const parsedCount = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(FacetFiltersForm.selectors.productCount);
    const container = document.querySelector(
      FacetFiltersForm.selectors.productCount
    );
    if (!parsedCount || !container) return;
    container.innerHTML = parsedCount.innerHTML;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.f-facets__block[data-index="${element.dataset.index}"]`
      );
      if (!target) return;
      target.textContent = "";
      const child =
        element.querySelector("collapsible-tab") ||
        element.querySelector("#Mobile-SortBy-Block");
      if (child) target.appendChild(child);
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const current = document.querySelector(selector);
      if (!activeFacetsElement || !current) return;
      current.innerHTML = activeFacetsElement.innerHTML;
    });
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: FacetFiltersForm.sectionId,
      },
    ];
  }

  createSearchParams(form, changedControl) {
    const searchParams = new URLSearchParams(window.location.search);

    if (changedControl && changedControl.name) {
      const name = changedControl.name;
      const value = changedControl.value;

      if (changedControl.type === "checkbox") {
        const remainingValues = searchParams
          .getAll(name)
          .filter((currentValue) => currentValue !== value);
        searchParams.delete(name);
        remainingValues.forEach((currentValue) =>
          searchParams.append(name, currentValue)
        );
        if (changedControl.checked && !remainingValues.includes(value)) {
          searchParams.append(name, value);
        }
      } else if (changedControl.type === "radio") {
        if (changedControl.checked) searchParams.set(name, value);
      } else if (value === "") {
        searchParams.delete(name);
      } else {
        searchParams.set(name, value);
      }

      return searchParams.toString();
    }

    const formData = new FormData(form);
    if (form.id === "FacetFiltersForm" && !FoxThemeSettings.isMobile) {
      formData.delete("sort_by");
    }
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    const form = event.target.closest("form");
    if (!(form instanceof HTMLFormElement)) return;
    this.onSubmitForm(this.createSearchParams(form, event.target), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const hrefParams =
      link.href.indexOf("?") === -1
        ? ""
        : link.href.slice(link.href.indexOf("?") + 1);

    const isClearAll =
      link.classList.contains("active-facets__button-clear") ||
      Boolean(link.closest(".f-facets__footer"));

    if (isClearAll) {
      FacetFiltersForm.renderPage(hrefParams);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const filterParam = link.dataset.filterParam;
    const filterValue = link.dataset.filterValue;

    if (filterParam && filterValue !== undefined) {
      const remainingValues = searchParams
        .getAll(filterParam)
        .filter((currentValue) => currentValue !== filterValue);
      searchParams.delete(filterParam);
      remainingValues.forEach((currentValue) =>
        searchParams.append(filterParam, currentValue)
      );
      FacetFiltersForm.renderPage(searchParams.toString());
      return;
    }

    const priceParams = [
      link.dataset.filterMinParam,
      link.dataset.filterMaxParam,
    ].filter(Boolean);
    if (priceParams.length) {
      priceParams.forEach((param) => searchParams.delete(param));
      FacetFiltersForm.renderPage(searchParams.toString());
      return;
    }

    FacetFiltersForm.renderPage(hrefParams);
  }
}
FacetFiltersForm.selectors = {
  container: ".f-facets__container",
  productGrid: "[data-products-grid]",
  productCount: "[data-product-count]",
  filtersForm: "[data-facet-form]",
  facetWrapper: "[data-facets-wrapper]",
  facetBlock: ".f-facets__block",
};

FacetFiltersForm.section = document.querySelector("#ProductGridContainer");
FacetFiltersForm.sectionId =
  FacetFiltersForm.section && FacetFiltersForm.section.dataset.sectionId;
FacetFiltersForm.domNodes = queryDomNodes(
  FacetFiltersForm.selectors,
  FacetFiltersForm.section
);
FacetFiltersForm.loading = new window.FoxTheme.AnimateLoading(document.body, {
  overlay: document.querySelector(FacetFiltersForm.selectors.filtersForm),
});

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      inputs: ['input[type="number"]'],
      ranges: ['input[type="range"]'],
      minRange: '[data-type="min-range"]',
      maxRange: '[data-type="max-range"]',
      minInput: '[data-type="min-input"]',
      maxInput: '[data-type="max-input"]',
    };
    this.domNodes = queryDomNodes(this.selectors, this);
    this.priceGap = parseInt(this.dataset.priceGap);
    this.priceMax = parseInt(this.dataset.priceMax);

    this.domNodes.inputs.forEach((el) =>
      el.addEventListener("change", this.onInputChange.bind(this))
    );
    this.domNodes.ranges.forEach((el) =>
      el.addEventListener("input", this.onSliderChange.bind(this))
    );

    this.setMinAndMaxValues();
  }

  onSliderChange(event) {
    const input = event.currentTarget;
    const type = input.dataset.type;
    const value = Number(input.value);
    const { minInput, maxInput, minRange, maxRange } = this.domNodes;
    if (type === "min-range") {
      if (maxRange.value - input.value >= this.priceGap) {
        minInput.value = input.value;
      } else {
        input.value = maxRange.value - this.priceGap;
      }
    }
    if (type === "max-range") {
      if (input.value - minRange.value >= this.priceGap) {
        maxInput.value = input.value;
      } else {
        input.value = Number(minRange.value) + Number(this.priceGap);
      }
    }
    this.style.setProperty(
      "--price-min",
      (100 * minRange.value) / this.priceMax + "%"
    );
    this.style.setProperty(
      "--price-max",
      (100 * maxRange.value) / this.priceMax + "%"
    );
  }

  onInputChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const { minInput, maxInput } = this.domNodes;
    if (maxInput.value)
      minInput.setAttribute("max", Number(maxInput.value) - this.priceGap);
    if (minInput.value)
      maxInput.setAttribute("min", Number(minInput.value) + this.priceGap);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("a").addEventListener("click", (e) => {
      e.preventDefault();
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      if (form) form.onActiveFilterClick(e);
    });
  }
}

customElements.define("facet-remove", FacetRemove);

class ApplyButton extends HTMLElement {
  constructor() {
    super();
    const drawer = this.closest("drawer-component");
    this.querySelector("button").addEventListener("click", (e) => {
      e.preventDefault();
      drawer && drawer.closeDrawer(false);
    });
  }
}

customElements.define("facet-apply", ApplyButton);
